// Pure pagination math for the bloodwork-analysis PDF's "Holobiome Roadmap"
// block — the five kit boxes and the Add-On Products table.
//
// WHY this exists rather than a page-break-inside rule: html2pdf measures page
// breaks against `pageSize.inner.px.height` (1073px for this report) but slices
// the rendered canvas against `floor(canvas.width * inner.ratio)` (1074px). The
// two grids drift by 1px per page, so this deep into a long report its
// keep-together pass believes a block sits safely inside a page while the
// slicer cuts straight through it. Every long section here is therefore
// pre-paginated in JS (see pdfMooPagination, pdfSupplementsPagination, and the
// section packer in bloodwork-analysis-pdf.tsx): budget each page well under
// its true capacity, force the break explicitly, and let the slack absorb the
// drift.
//
// Items here are ATOMIC — a row of kit cards, or one add-on table row. Neither
// can be sliced the way a run of product bullets can, so an item that is taller
// than a whole page lands alone on its own page and is allowed to overflow
// rather than being split. In practice nothing comes close: the tallest kit card
// is six products.
//
// Heights are caller-provided estimates (pixels). The packer is pure — no DOM
// measurement — which keeps it deterministic and testable.

export type HolobiomePage<T> = {
  items: T[];
  // Estimated height of the rendered page, card overhead included. The PDF
  // container uses the LAST page's height to decide whether the Disclaimer
  // fits below the roadmap or needs a page of its own.
  height: number;
};

export type HolobiomePaginationInput<T> = {
  items: T[];
  // Estimated height of each item, index-aligned with `items`.
  heights: number[];
  // Usable height per page. Deliberately well below the true page height —
  // that margin is what absorbs the grid drift described above.
  pageCapacity: number;
  // Card header + body padding, charged once per page.
  cardOverhead: number;
  // Extra height charged to the FIRST page only (the legend above the kits,
  // the intro line above the add-on table).
  firstPageExtra?: number;
  // Vertical gap between two items on the same page.
  itemGap?: number;
};

export function packHolobiomeItems<T>(
  input: HolobiomePaginationInput<T>,
): HolobiomePage<T>[] {
  const {
    items,
    heights,
    pageCapacity,
    cardOverhead,
    firstPageExtra = 0,
    itemGap = 0,
  } = input;

  const pages: HolobiomePage<T>[] = [];
  let page: T[] = [];
  let used = cardOverhead + firstPageExtra;

  items.forEach((item, i) => {
    const cost = (heights[i] ?? 0) + (page.length > 0 ? itemGap : 0);
    // Guarded on a non-empty page so an item taller than a whole page still
    // lands somewhere instead of looping forever on an empty one.
    if (page.length > 0 && used + cost > pageCapacity) {
      pages.push({ items: page, height: used });
      page = [];
      used = cardOverhead;
      page.push(item);
      used += heights[i] ?? 0;
      return;
    }
    page.push(item);
    used += cost;
  });

  if (page.length > 0) pages.push({ items: page, height: used });
  return pages;
}
